import { ScheduleModel } from '@maxpreps/apiserviceclients/apiModels';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import SmartAnchor, {
  ISmartAnchorProps
} from '@maxpreps/shared/corecomponents/Anchor/SmartAnchor';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import React from 'react';
import getCSRRewriteAttributes, {
  ICSRAnchorAttributes
} from '../../utils/getCSRRewriteAttributes';

interface ITeamSmartAnchorProps extends ISmartAnchorProps {
  schoolId?: string;
  ssid?: string;
  allSeasonId?: string;
  isAdmin?: boolean;
}

export interface ITeamSmartAnchorOptions {
  schoolId: string;
  ssid: string;
  allSeasonId: string;
  isAdmin?: boolean;
  appType: AppType;
  scheduleModel: ScheduleModel;
}

const getTeamAnchorProps = (
  href,
  options: ITeamSmartAnchorOptions
): ICSRAnchorAttributes => {
  if (!href) return { href };

  const { schoolId, ssid, allSeasonId, isAdmin, appType, scheduleModel } =
    options;

  return getCSRRewriteAttributes(
    href,
    schoolId,
    ssid,
    allSeasonId,
    appType,
    isAdmin,
    scheduleModel
  );
};

const TeamSmartAnchor = React.forwardRef<
  HTMLAnchorElement,
  ITeamSmartAnchorProps
>((props, ref) => {
  const { href, ssid, schoolId, allSeasonId, isAdmin, ...rest } = props;

  const teamContext = useTeamContext();
  const { appType } = useRequestContext();

  // This makes the component SUPER opinionated. It will assume
  // 1. You're trying to link to this team unless you explicitly pass an href
  // 2. You are creating links it assumes the a schoolid, ssid, and allseasonid as the current team. You must be explicit to change them
  // Both are probably true more often than not so this is easy to code but also when not
  const {
    canonicalUrl: defaultHref,
    sportSeasonId: defaultSSID,
    teamId: defaultSchoolId,
    allSeasonId: defaultAllSeasonId
  } = teamContext.data;

  // unfortunately sportData.scheduleModel isn't a ScheduleModel
  // it's a string so we are going to remap it
  const { scheduleModel: scheduleModelKey } =
    teamContext.sportData as never as {
      scheduleModel: keyof ScheduleModel;
    };

  const teamAnchorProps = getTeamAnchorProps(
    // if we didn't explcitly set a href assume we are using this teams
    href ?? defaultHref,
    {
      ssid: ssid ?? defaultSSID,
      schoolId: schoolId ?? defaultSchoolId,
      allSeasonId: allSeasonId ?? defaultAllSeasonId,
      isAdmin: !!isAdmin,
      appType,
      scheduleModel: ScheduleModel[scheduleModelKey]
    }
  );

  // @ts-ignore
  return <SmartAnchor ref={ref} {...rest} {...teamAnchorProps} />;
});

TeamSmartAnchor.displayName = 'TeamSmartAnchor';
export default TeamSmartAnchor;
